/**
 * A module, as a page.
 *
 * The card in a feed shows a playlist; this is the playlist *itself* — the same
 * row of tiles, larger, with the course's own facts around it and the one thing a
 * reader came to do.
 *
 * Five decisions shape it:
 *
 *  - **The spacing is explicit.** Every block sits in a column with its own gap
 *    rather than relying on the scroll view's content container: on web,
 *    `Layout.Content` wraps its children in a centring `View`, so a `gap` set on the
 *    container has no siblings to space — which is why the first build of this
 *    screen had three card-wide buttons with *zero* pixels between them (measured
 *    2026-09-30: `y=398, y=442, y=486`, each 44 tall, touching).
 *  - **It is played as a sequence, and that is what the numbers are for.** Each
 *    tile carries its place, and pressing one starts the run *there* — a learner
 *    who has already done the first three is not made to sit through them again.
 *  - **The way in is one button, and what it says depends on who is reading.**
 *    A learner is offered the challenge their classmates have already set;
 *    everybody else is offered the way in (see `#/lib/moduleAudience`, and the
 *    block that draws it).
 *  - **The results have their own door**, quietly, under the way in — the same
 *    arrangement the card uses, for the same reason: the learner and the teacher
 *    want opposite things from the same course.
 *  - **A draft says so, on the page.** A module that is still being assembled is
 *    only ever opened by the person assembling it, and what they need is not a
 *    general "unpublished" notice but the next thing to do: how many capsules are
 *    still missing, and the way into the builder.
 */
import {useEffect, useMemo} from 'react'
import {View} from 'react-native'
import {Plural, Trans, useLingui} from '@lingui/react/macro'
import {useNavigation} from '@react-navigation/native'

import {moduleCreatorOf} from '#/lib/modulePostCard'
import {
  modulePreviewStateOf,
  readModulePreviewMode,
  readModulePreviewState,
  writeModulePreviewState,
} from '#/lib/moduleFixtures'
import {moduleUriFromPath} from '#/lib/moduleRecord'
import {
  MODULE_MIN_CAPSULES,
  moduleCoinCount,
  modulePublishBlockers,
  moduleReadyCount,
  moduleSlots,
} from '#/lib/modulePlaylist'
import {type ModuleRunProgress} from '#/lib/moduleRun'
import {
  type CommonNavigatorParams,
  type NativeStackScreenProps,
} from '#/lib/routes/types'
import {useActorCapsulesQuery} from '#/state/queries/capsule'
import {useModuleByUriQuery} from '#/state/queries/module'
import {useProfileQuery} from '#/state/queries/profile'
import {useCurrentAccountProfile} from '#/state/queries/useCurrentAccountProfile'
import {useSession} from '#/state/session'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonIcon, ButtonText} from '#/components/Button'
import {Check_Stroke2_Corner0_Rounded as Check} from '#/components/icons/Check'
import {ChevronRight_Stroke2_Corner0_Rounded as ChevronRight} from '#/components/icons/Chevron'
import {Pencil_Stroke2_Corner0_Rounded as Pencil} from '#/components/icons/Pencil'
import * as Layout from '#/components/Layout'
import {MediaInsetBorder} from '#/components/MediaInsetBorder'
import {ModuleAudience} from '#/components/modules/ModuleAudience'
import {ModuleCaseSwitcher} from '#/components/modules/ModuleCaseSwitcher'
import {ModuleFactsRow} from '#/components/modules/ModuleFactsRow'
import {ModulePlaylistCarousel} from '#/components/modules/ModulePlaylistCarousel'
import {useModuleAudience} from '#/components/modules/useModuleAudience'
import {useModuleRunWithPreview} from '#/components/modules/useModulePreviewRun'
import {useModuleTiles} from '#/components/modules/useModuleTiles'
import {Text} from '#/components/Typography'
import {UserAvatar} from '#/view/com/util/UserAvatar'
import {IS_DEV} from '#/env'

type Props = NativeStackScreenProps<CommonNavigatorParams, 'Module'>

export function ModuleScreen({route, navigation}: Props) {
  /*
   * The address comes off a URL, where it is percent-encoded (see
   * `moduleUriFromPath`), and is decoded once here — before the query, and before
   * `moduleCreatorOf` reads the author out of it.
   */
  const moduleUri = moduleUriFromPath(route.params.moduleUri) ?? ''
  const t = useTheme()
  const {t: l} = useLingui()
  const {currentAccount} = useSession()

  const {data: record, isPending, isError} = useModuleByUriQuery(moduleUri)

  const authorDid = useMemo(() => moduleCreatorOf(moduleUri), [moduleUri])
  const {data: author} = useProfileQuery({did: authorDid ?? undefined})
  const isMine = !!currentAccount?.did && currentAccount.did === authorDid
  /*
   * The reader's own face, for the one case that is about them alone: a learner of
   * this module's level reading a module nobody has played is drawn in the class
   * row themselves (see `ModuleAudience`).
   */
  const me = useCurrentAccountProfile()
  const viewerFace = useMemo(
    () => (me ? {did: me.did, avatar: me.avatar} : undefined),
    [me],
  )

  const items = useMemo(() => record?.items ?? [], [record])
  /*
   * The creator's own records, handed to the tiles.
   *
   * Only for a module of one's own that is still being assembled: that is the one
   * case where a place in the playlist can be a *draft* rather than a capsule with
   * a post, and the record is what tells the tile it is a draft to be published
   * rather than a capsule that has gone.
   */
  const {data: myCapsules} = useActorCapsulesQuery(
    isMine && record?.status === 'draft' ? (authorDid ?? undefined) : undefined,
  )
  const {tiles} = useModuleTiles(
    items,
    isMine && record?.status === 'draft' ? myCapsules : undefined,
  )

  /*
   * How far this device's learner has got.
   *
   * The run is remembered locally (there is no plays endpoint yet, see
   * `#/state/moduleRuns`), and it is what makes the playlist read as a course
   * somebody is part way through rather than a list of six equal things: the tiles
   * that are done are marked, and the way in offers *Continue module* rather than
   * *Play module*.
   */
  const {progress, done} = useModuleRunWithPreview({moduleUri, items})
  const played = useMemo(() => new Set(done), [done])

  const slots = useMemo(
    () =>
      moduleSlots({
        items,
        capsules: items
          .map(item => myCapsules?.find(c => c.uri === item.uri))
          .filter((c): c is NonNullable<typeof c> => !!c),
        level: record?.level,
        subject: record?.subject,
      }),
    [items, myCapsules, record?.level, record?.subject],
  )
  const ready = moduleReadyCount(slots)
  const coins = useMemo(() => moduleCoinCount(myCapsules ?? []), [myCapsules])
  const blockers = useMemo(
    () =>
      record
        ? modulePublishBlockers({
            title: record.title,
            level: record.level,
            subject: record.subject,
            items: record.items,
            capsules: myCapsules ?? [],
          })
        : [],
    [myCapsules, record],
  )

  /*
   * The preview, in a dev build only — and never held here.
   *
   * All of it lives in one store (`#/lib/moduleFixtures`) that every screen which
   * draws a module reads, so the reel at the top of this page, the reel in a post's
   * header and the card in the feed cannot disagree about which case is being
   * looked at.
   *
   * The route param only *seeds* it. `?preview=board` is a way to hand somebody a
   * state, and it is written once into the same store everything else reads, rather
   * than being consulted here and forgotten by the card below.
   */
  /*
   * A `?preview=` param is a *seed*, and only while the preview is on: a link to
   * `?preview=board` opened in a build whose preview mode is off draws the real
   * class, rather than storing a fact that every reader below will refuse to read
   * (`#/lib/moduleFixtures`).
   */
  const routePreview =
    IS_DEV && readModulePreviewMode()
      ? modulePreviewStateOf(route.params.preview)
      : null
  useEffect(() => {
    if (routePreview) writeModulePreviewState(routePreview)
  }, [routePreview])

  const {audience, allCases} = useModuleAudience({
    moduleUri,
    authorDid,
    level: record?.level ?? null,
    subject: record?.subject ?? null,
  })

  /*
   * The way to the results, which one thing carries: the class's own faces, for
   * every reader (`ModuleAudience` — a learner's year or a teacher's board, one
   * pressable strip).
   *
   * The invented class travels *with* the address, because the leaderboard's screen
   * reads its board from its own route: a page showing a crowd of ninety-six and a
   * board showing the class's real plays would be two answers to one question.
   */
  const onOpenBoard = useMemo(
    () => () =>
      navigation.navigate('ModuleLeaderboard', {
        moduleUri,
        preview: IS_DEV ? (readModulePreviewState() ?? undefined) : undefined,
      }),
    [moduleUri, navigation],
  )

  const onPlay = useMemo(
    () => () => navigation.navigate('ModulePlay', {moduleUri}),
    [moduleUri, navigation],
  )

  if (isError) {
    return (
      <Layout.Screen testID="moduleScreen">
        <Layout.Header.Outer>
          <Layout.Header.BackButton />
          <Layout.Header.Content>
            <Layout.Header.TitleText>
              <Trans>Module</Trans>
            </Layout.Header.TitleText>
          </Layout.Header.Content>
          <Layout.Header.Slot />
        </Layout.Header.Outer>
        <Layout.Content>
          <Text style={[a.text_md, a.p_2xl, t.atoms.text_contrast_medium]}>
            <Trans>
              This module could not be loaded. Try again in a moment.
            </Trans>
          </Text>
        </Layout.Content>
      </Layout.Screen>
    )
  }

  const isDraft = record?.status === 'draft'

  return (
    <Layout.Screen testID="moduleScreen">
      <Layout.Header.Outer>
        <Layout.Header.BackButton />
        <Layout.Header.Content>
          <Layout.Header.TitleText>
            <Trans>Module</Trans>
          </Layout.Header.TitleText>
        </Layout.Header.Content>
        {isMine ? (
          <Layout.Header.Slot>
            <Button
              testID="moduleEdit"
              label={l`Edit this module`}
              size="small"
              color="secondary"
              onPress={() =>
                navigation.navigate('ModuleStudio', {draftUri: moduleUri})
              }>
              <ButtonIcon icon={Pencil} />
              <ButtonText>
                <Trans>Edit</Trans>
              </ButtonText>
            </Button>
          </Layout.Header.Slot>
        ) : (
          <Layout.Header.Slot />
        )}
      </Layout.Header.Outer>

      <Layout.Content
        testID="moduleContent"
        contentContainerStyle={[a.px_md, a.pb_4xl]}>
        {/*
          One column, with the gaps written down.

          `Layout.Content`'s own `gap` would have nothing to space on web (see the
          note at the top of this file), so the page's rhythm is a real `View`.
        */}
        <View style={[a.gap_lg, a.pt_sm]}>
          {/*
            The dev reel: one case at a time, every case in the list — invented
            plays, the reader's seat, a friend in the class and the run, in one
            press each. Draws nothing outside a dev build.
          */}
          <ModuleCaseSwitcher testID="moduleCaseSwitcher" />

          <View style={[a.gap_sm]}>
            {/* Who set the course. */}
            <View style={[a.flex_row, a.align_center, a.gap_sm]}>
              <View
                style={[a.relative, a.rounded_full, {width: 34, height: 34}]}>
                <UserAvatar type="user" size={34} avatar={author?.avatar} />
                <MediaInsetBorder />
              </View>
              <View style={[a.flex_1]}>
                <Text
                  numberOfLines={1}
                  style={[a.text_sm, a.font_bold, t.atoms.text]}>
                  {author?.displayName || (author ? `@${author.handle}` : '')}
                </Text>
                {author?.displayName ? (
                  <Text
                    numberOfLines={1}
                    style={[a.text_xs, t.atoms.text_contrast_medium]}>
                    {`@${author.handle}`}
                  </Text>
                ) : null}
              </View>
              {isDraft ? (
                <View
                  testID="moduleDraftChip"
                  style={[styles.chip, t.atoms.bg_contrast_50]}>
                  <Text style={[a.text_xs, a.font_bold, t.atoms.text]}>
                    <Trans>Draft</Trans>
                  </Text>
                </View>
              ) : null}
            </View>

            <Text
              testID="moduleTitle"
              style={[a.text_3xl, a.font_bold, a.leading_tight, t.atoms.text]}>
              {record?.title || l`Module`}
            </Text>

            {record?.description ? (
              <Text style={[a.text_md, a.leading_snug, t.atoms.text]}>
                {record.description}
              </Text>
            ) : null}

            <ModuleFactsRow
              testID="moduleFacts"
              capsuleCount={isDraft ? ready : record ? items.length : null}
              subject={record?.subject ?? null}
              level={record?.level ?? null}
              coins={coins > 0 ? coins : null}
            />
          </View>

          {items.length > 0 ? (
            <ModulePlaylistCarousel
              tiles={tiles}
              testID="modulePlaylist"
              tileTestIDPrefix="moduleTile"
              accessibilityHint={l`Plays the module from that capsule`}
              onPressTile={tile =>
                navigation.navigate('ModulePlay', {
                  moduleUri,
                  start: tile.position - 1,
                })
              }
              renderTileExtra={tile =>
                played.has(tile.uri) ? (
                  <View
                    testID={`moduleTilePlayed-${tile.position}`}
                    style={styles.playedMark}>
                    <Check size="sm" fill="white" />
                  </View>
                ) : null
              }
            />
          ) : null}

          {isDraft ? (
            <View
              testID="moduleDraftNote"
              style={[styles.note, t.atoms.bg_contrast_25]}>
              <Text style={[a.text_sm, t.atoms.text]}>
                {blockers.length === 0 ? (
                  <Trans>
                    This module is ready. Publish it to put it on your profile
                    and let your class play it.
                  </Trans>
                ) : (
                  <Trans>
                    A module is published with {MODULE_MIN_CAPSULES} published
                    capsules, all of the same subject and level. Open the
                    builder to finish it.
                  </Trans>
                )}
              </Text>
            </View>
          ) : null}

          {/*
            The way in, and who the reader is up against.

            One block, one primary action — and the word on it belongs to the
            reader's role rather than to this screen.
          */}
          <ModuleAudience
            testID="moduleAudience"
            audience={audience}
            title={record?.title}
            viewerFace={viewerFace}
            progress={progress}
            allCases={allCases}
            onOpenBoard={onOpenBoard}
            onPlay={onPlay}
          />

          {isMine ? (
            <View style={[a.gap_xs]}>
              <Button
                testID="moduleOpenBuilder"
                label={l`Open the builder`}
                size="large"
                color="secondary"
                onPress={() =>
                  navigation.navigate('ModuleStudio', {draftUri: moduleUri})
                }>
                <ButtonIcon icon={ChevronRight} />
                <ButtonText>
                  <Trans>Add or reorder capsules</Trans>
                </ButtonText>
              </Button>
              <Text
                style={[
                  a.text_xs,
                  a.text_center,
                  t.atoms.text_contrast_medium,
                ]}>
                <Plural
                  value={MODULE_MIN_CAPSULES}
                  one="A module is published with at least # capsule."
                  other="A module is published with at least # capsules."
                />
              </Text>
            </View>
          ) : null}

          {isPending ? (
            <Text style={[a.text_sm, t.atoms.text_contrast_medium]}>
              <Trans>Loading…</Trans>
            </Text>
          ) : null}
        </View>
      </Layout.Content>
    </Layout.Screen>
  )
}

const styles = {
  chip: {
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 999,
  } as const,
  note: {
    padding: 12,
    borderRadius: 10,
  } as const,
  /*
   * A finished capsule's mark, on the tile it belongs to.
   *
   * Bottom-right, away from the order chip (top-left) and the coin chip (top-right)
   * the tile already carries, so a played capsule is legible without hiding either
   * of the two things a tile is for.
   */
  playedMark: {
    position: 'absolute',
    right: 6,
    bottom: 6,
    width: 22,
    height: 22,
    borderRadius: 11,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
  } as const,
}
